<template>
  <div class="cs3-modal-overlay" @click.self="$emit('close')">
    <div
      class="cs3-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notify-wishlist-title"
      tabindex="-1"
    >
      <div class="cs3-modal__header">
        <h4 id="notify-wishlist-title">Notify Wishlist</h4>
        <a href="javascript:void(0)" class="cs3-modal__close" @click="$emit('close')">&times;</a>
      </div>

      <div class="cs3-modal__body">
        <div v-if="loading">Loading filters…</div>

        <div v-else-if="results">
          <p>
            <strong>Sent to {{ matched }} {{ matched === 1 ? 'person' : 'people' }}.</strong>
          </p>
          <ul class="cs3-summary">
            <li v-for="ch in summaryChannels" :key="ch.name" class="cs3-summary__row">
              <strong>{{ ch.label }}:</strong>
              <span>{{ ch.sent }} sent</span>
              <span v-if="ch.failed > 0" class="cs3-summary__failed">
                · {{ ch.failed }} failed{{ ch.reasonText ? ' (' + ch.reasonText + ')' : '' }}
              </span>
            </li>
            <li v-if="summaryChannels.length === 0" class="cs3-empty">
              No channels were attempted.
            </li>
          </ul>
          <button class="c-solid-btn c-solid-btn--small c-solid-btn--green" @click="$emit('close')">Done</button>
        </div>

        <div v-else>
          <p class="cs3-help">Pick the statuses and skills to notify. We'll send one
             link per matching person (no duplicates).</p>

          <label class="cs3-expiry">Link expires in
            <select v-model.number="expiryHours">
              <option :value="12">12 hours</option>
              <option :value="24">24 hours</option>
              <option :value="36">36 hours</option>
            </select>
          </label>

          <!-- Delivery channels — mirrors the phone-sheet send model.
               All three are pre-checked; dispatcher can drop any of
               them. The backend fires every selected channel that the
               recipient has contact for. -->
          <fieldset class="cs3-channels">
            <legend>Send via</legend>
            <label class="cs3-channels__opt">
              <input type="checkbox" v-model="channelSms" /> SMS
            </label>
            <label class="cs3-channels__opt">
              <input type="checkbox" v-model="channelEmail" /> Email
            </label>
            <label class="cs3-channels__opt">
              <input type="checkbox" v-model="channelPush" /> Push
            </label>
          </fieldset>

          <label class="cs3-note">Note to recipient (optional)
            <textarea
              v-model="noteToRecipient"
              rows="2"
              maxlength="500"
              placeholder="e.g. 'Wednesday call, please respond by noon.' — appears in the SMS / email body"
            ></textarea>
            <small class="cs3-note__hint">{{ noteToRecipient.length }} / 500</small>
          </label>

          <div class="cs3-filter-grid">
            <section class="cs3-filter">
              <header>
                <strong>Statuses</strong>
                <span class="cs3-filter__tools">
                  <a href="javascript:void(0)" @click="setAllStatuses(true)">All</a> ·
                  <a href="javascript:void(0)" @click="setAllStatuses(false)">None</a>
                </span>
              </header>
              <ul class="cs3-checklist">
                <li v-for="s in statuses" :key="s.id">
                  <label>
                    <input type="checkbox" v-model="s.selected" />
                    {{ s.name }} <small>({{ s.count }})</small>
                  </label>
                </li>
                <li v-if="statuses.length === 0" class="cs3-empty">No statuses defined.</li>
              </ul>
            </section>

            <section class="cs3-filter">
              <header>
                <strong>Skills</strong>
                <span class="cs3-filter__tools">
                  <a href="javascript:void(0)" @click="setAllSkillsInView(true)">All</a> ·
                  <a href="javascript:void(0)" @click="setAllSkillsInView(false)">None</a> ·
                  <a href="javascript:void(0)" @click="resetSkillsToJob">Job</a>
                </span>
              </header>
              <div class="cs3-search">
                <input
                  v-model="skillSearch"
                  type="search"
                  placeholder="Search skills…"
                  aria-label="Search skills"
                />
                <a v-if="skillSearch" href="javascript:void(0)" class="cs3-search__clear"
                   @click="skillSearch = ''" aria-label="Clear search">&times;</a>
              </div>
              <ul class="cs3-checklist">
                <li v-for="k in filteredSkills" :key="k.id" :class="{ 'cs3-in-job': k.is_in_job }">
                  <label>
                    <input type="checkbox" v-model="k.selected" />
                    {{ k.name }} <small>({{ k.count }})</small>
                    <span v-if="k.is_in_job" class="cs3-tag">in&nbsp;job</span>
                  </label>
                </li>
                <li v-if="skills.length === 0" class="cs3-empty">No skills defined.</li>
                <li v-else-if="filteredSkills.length === 0" class="cs3-empty">
                  No matches for &ldquo;{{ skillSearch }}&rdquo;.
                </li>
              </ul>
              <p v-if="skillSearch && hiddenSelectedSkillCount > 0" class="cs3-filter__note">
                {{ hiddenSelectedSkillCount }} selected skill{{ hiddenSelectedSkillCount === 1 ? '' : 's' }} hidden by search.
              </p>
            </section>
          </div>

          <p v-if="error" class="report-error">{{ error }}</p>

          <button
            class="c-solid-btn c-solid-btn--small c-solid-btn--green"
            :disabled="sending
              || selectedStatusIds.length === 0
              || selectedSkillIds.length === 0
              || selectedChannels.length === 0"
            @click="send">
            {{ sendLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import service from '../../services.js';

export default {
  name: 'NotifyWishlistModal',
  props: {
    jobId: { type: String, required: true },
  },
  data() {
    return {
      statuses: [],
      skills: [],
      skillSearch: '',
      noteToRecipient: '',
      expiryHours: 24,
      channelSms: true,
      channelEmail: true,
      channelPush: true,
      loading: true,
      sending: false,
      error: null,
      results: null,
      matched: 0,
      summary: {},
    };
  },
  computed: {
    selectedStatusIds() {
      return this.statuses.filter(s => s.selected).map(s => s.id);
    },
    selectedSkillIds() {
      return this.skills.filter(k => k.selected).map(k => k.id);
    },
    selectedChannels() {
      const out = [];
      if (this.channelSms)   out.push('sms');
      if (this.channelEmail) out.push('email');
      if (this.channelPush)  out.push('push');
      return out;
    },
    filteredSkills() {
      const q = this.skillSearch.trim().toLowerCase();
      if (!q) return this.skills;
      return this.skills.filter(k => (k.name || '').toLowerCase().indexOf(q) !== -1);
    },
    hiddenSelectedSkillCount() {
      if (!this.skillSearch.trim()) return 0;
      const visibleIds = new Set(this.filteredSkills.map(k => k.id));
      return this.skills.filter(k => k.selected && !visibleIds.has(k.id)).length;
    },
    sendLabel() {
      if (this.sending) return 'Sending…';
      if (this.selectedStatusIds.length === 0) return 'Pick at least one status';
      if (this.selectedSkillIds.length === 0) return 'Pick at least one skill';
      if (this.selectedChannels.length === 0) return 'Pick at least one channel';
      return 'Send notifications';
    },
    // Reshape the per-channel summary from the API response into a
    // template-friendly list. Keeps the SMS / Email / Push order.
    summaryChannels() {
      const labels = { sms: 'SMS', email: 'Email', push: 'Push' };
      const order = ['sms', 'email', 'push'];
      const out = [];
      order.forEach((key) => {
        const row = this.summary && this.summary[key];
        if (!row) return;
        const reasons = row.reasons || {};
        const reasonText = Object.keys(reasons)
          .map(r => r + ': ' + reasons[r])
          .join(', ');
        out.push({
          name: key,
          label: labels[key] || key,
          sent: row.sent || 0,
          failed: row.failed || 0,
          reasonText,
        });
      });
      return out;
    },
  },
  mounted() {
    this.loadFilters();
    this._onEsc = (e) => { if (e.key === 'Escape') this.$emit('close'); };
    window.addEventListener('keydown', this._onEsc);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this._onEsc);
  },
  methods: {
    async loadFilters() {
      this.loading = true;
      this.error = null;
      try {
        const res = await service.getUrl('/api/v3/wishlist-notify/jobs/' + this.jobId + '/recipients');
        const payload = (res && res.data && res.data.data) || {};
        const statusList = payload.statuses || [];
        const skillList = payload.skills || [];
        // Defaults per #1151 reshape:
        //   - statuses: all pre-checked
        //   - skills: pre-checked when is_in_job=true (job's skill needs)
        this.statuses = statusList.map(s => Object.assign({}, s, { selected: true }));
        this.skills = skillList.map(k => Object.assign({}, k, { selected: !!k.is_in_job }));
      } catch (e) {
        this.error = this.extractError(e, 'Failed to load filters');
      } finally {
        this.loading = false;
      }
    },
    setAllStatuses(value) {
      this.statuses.forEach(s => { s.selected = value; });
    },
    // All / None operate on the currently visible (search-filtered) skill
    // subset. Skills outside the filter keep whatever selection state
    // they already had — that's why "X selected skills hidden by search"
    // is surfaced under the list while a search is active.
    setAllSkillsInView(value) {
      const visibleIds = new Set(this.filteredSkills.map(k => k.id));
      this.skills.forEach(k => {
        if (visibleIds.has(k.id)) k.selected = value;
      });
    },
    resetSkillsToJob() {
      this.skills.forEach(k => { k.selected = !!k.is_in_job; });
    },
    async send() {
      this.sending = true;
      this.error = null;
      try {
        const payload = {
          status_ids: this.selectedStatusIds,
          skill_ids: this.selectedSkillIds,
          expiry_hours: this.expiryHours,
          note_to_recipient: this.noteToRecipient ? String(this.noteToRecipient).trim() : null,
          channels: this.selectedChannels,
        };
        const res = await service.postUrl('/api/v3/wishlist-notify/jobs/' + this.jobId, payload);
        const body = (res && res.data) || {};
        this.results = body.data || [];
        this.matched = typeof body.matched === 'number' ? body.matched : this.results.length;
        this.summary = body.summary || {};
      } catch (e) {
        this.error = this.extractError(e, 'Send failed');
      } finally {
        this.sending = false;
      }
    },
    extractError(e, fallback) {
      const d = e && e.response && e.response.data;
      if (d && d.error) return d.error;
      if (d && d.errors) return Object.values(d.errors).flat().join('; ');
      return (e && e.message) || fallback;
    },
  },
};
</script>

<style scoped>
.cs3-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center; z-index: 1050; }
.cs3-modal { background: #fff; border-radius: 4px; width: 700px; max-width: 96vw;
  max-height: 90vh; display: flex; flex-direction: column; font-size: 15px; }
.cs3-modal__header { display: flex; justify-content: space-between; align-items: center;
  padding: 14px 18px; border-bottom: 1px solid #e5e5e5; }
.cs3-modal__header h4 { margin: 0; font-size: 18px; }
.cs3-modal__close { font-size: 24px; line-height: 1; color: #888; text-decoration: none; }
.cs3-modal__body { padding: 18px; overflow-y: auto; }
.cs3-help { font-size: 15px; line-height: 1.4; margin: 0 0 12px; color: #444; }
.cs3-expiry { font-size: 15px; }
.cs3-expiry select { font-size: 15px; padding: 4px 6px; margin-left: 6px; }
.cs3-note { display: block; font-size: 15px; margin-top: 12px; }
.cs3-note textarea { width: 100%; box-sizing: border-box; padding: 8px;
  border: 1px solid #ccc; border-radius: 3px; font-family: inherit;
  font-size: 15px; margin-top: 4px; resize: vertical; }
.cs3-note__hint { display: block; text-align: right; color: #888; font-size: 12px; }

.cs3-filter-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 14px; }
.cs3-filter { border: 1px solid #ddd; border-radius: 4px; display: flex; flex-direction: column;
  min-height: 0; max-height: 420px; }
.cs3-filter header { display: flex; justify-content: space-between; align-items: center;
  padding: 8px 12px; border-bottom: 1px solid #eee; background: #fafafa; }
.cs3-filter header strong { font-size: 15px; }
.cs3-filter__tools { font-size: 14px; color: #555; }
.cs3-filter__tools a { color: #1976d2; }
.cs3-filter__note { padding: 6px 12px; margin: 0; font-size: 13px; color: #888;
  border-top: 1px solid #f3f3f3; background: #fafafa; }

.cs3-search { padding: 8px 10px; border-bottom: 1px solid #eee; position: relative; }
.cs3-search input { width: 100%; box-sizing: border-box; padding: 6px 26px 6px 8px;
  font-size: 14px; border: 1px solid #ccc; border-radius: 3px; }
.cs3-search__clear { position: absolute; right: 18px; top: 50%; transform: translateY(-50%);
  font-size: 18px; color: #888; text-decoration: none; line-height: 1; }

.cs3-checklist { list-style: none; padding: 0; margin: 0; overflow-y: auto; }
.cs3-checklist li { padding: 6px 12px; border-bottom: 1px solid #f3f3f3; font-size: 15px;
  line-height: 1.3; }
.cs3-checklist li label { display: flex; align-items: center; gap: 6px; cursor: pointer; }
.cs3-checklist li input[type="checkbox"] { width: 16px; height: 16px; }
.cs3-checklist li small { color: #777; font-size: 13px; }
.cs3-in-job { background: #f3f9f3; }
.cs3-tag { font-size: 11px; background: #2e8b57; color: #fff; border-radius: 2px;
  padding: 1px 6px; margin-left: 4px; vertical-align: middle; line-height: 1.3; }
.cs3-empty { color: #888; font-style: italic; font-size: 14px; }
.report-error { color: #c0392b; margin: 10px 0; font-size: 14px; }

.cs3-channels { border: 1px solid #ddd; border-radius: 4px;
  padding: 8px 12px; margin: 12px 0 0; display: flex; flex-wrap: wrap;
  gap: 14px; align-items: center; }
.cs3-channels legend { padding: 0 4px; font-size: 14px; color: #555;
  width: auto; border: 0; margin: 0; }
.cs3-channels__opt { display: inline-flex; align-items: center; gap: 6px;
  font-size: 15px; margin: 0; cursor: pointer; }
.cs3-channels__opt input[type="checkbox"] { width: 16px; height: 16px; }

.cs3-summary { list-style: none; padding: 0; margin: 12px 0 18px;
  border: 1px solid #e5e5e5; border-radius: 4px; }
.cs3-summary__row { padding: 8px 12px; border-bottom: 1px solid #f3f3f3;
  font-size: 15px; line-height: 1.4; display: flex; flex-wrap: wrap;
  gap: 6px; align-items: baseline; }
.cs3-summary__row:last-child { border-bottom: 0; }
.cs3-summary__failed { color: #c0392b; font-size: 14px; }

@media (max-width: 600px) {
  .cs3-filter-grid { grid-template-columns: 1fr; }
  .cs3-filter { max-height: 280px; }
  .cs3-channels { gap: 10px; }
}
</style>
